import React from "react";
import styled from "styled-components";
import {
  AlignCenterIcon,
  Divider,
  ZoomOutIcon,
  ZoomInIcon,
} from "./droppable/icons/Actions";
import Select, { components } from "@atlaskit/select";
import Tooltip from "@atlaskit/tooltip";
import eventBus from "../eventBus";

const Wrapper = styled.div`
  position: absolute;
  bottom: -80px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid rgba(9, 30, 66, 0.14);
  padding: 8px;
  gap: 4px;
`;

const Button = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;

  &:hover {
    background-color: rgba(9, 30, 66, 0.04);
    border-radius: 4px;
  }
`;

const customStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: 6,
    boxShadow: state.isFocused ? "0 0 0 2px rgba(76, 154, 255, 0.3)" : "none",
    minHeight: 36,
    height: 36,
    paddingLeft: 12,
    cursor: "pointer",
    justifyContent: "center",
  }),
  singleValue: (base) => ({
    ...base,
    fontWeight: 400,
    justifyContent: "center",
    fontSize: 14,
    textAlign: "center",
    width: "100%",
    maxWidth: "100%",
  }),
  placeholder: (base) => ({
    ...base,
    fontWeight: 400,
    justifyContent: "center",
    fontSize: 14,
    textAlign: "center",
    width: "100%",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: 4,
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: 6,
    boxShadow: "0 4px 8px rgba(9, 30, 66, 0.25)",
    marginTop: 4,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    fontWeight: 400,
    padding: "8px 12px",
    cursor: "pointer",
  }),
};

const ZOOM_TYPES = {
  ALIGN_MIDDLE: "align-middle",
  FIT: "fit",
  FULL: "full",
};

const ZOOM_OPTIONS = [
  { value: ZOOM_TYPES.FIT, label: "Zoom to Fit" },
  { value: ZOOM_TYPES.FULL, label: "Zoom to 100%" },
];

const ZOOM_STEP = 0.1;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2.5;

function ZoomController({ zoom, setZoom }) {
  const { SingleValue } = components;

  const CustomSingleValue = (props) => {
    const { data, children, ...rest } = props;
    let displayLabel = children;

    if (data.value === "full") {
      displayLabel = "100%";
    }

    return <SingleValue {...rest}>{displayLabel}</SingleValue>;
  };

  const handleZoom = (mode) => {
    if (mode === "in") {
      setZoom((prev) => Math.min(prev + ZOOM_STEP, ZOOM_MAX));
    } else if (mode === "out") {
      setZoom((prev) => Math.max(prev - ZOOM_STEP, ZOOM_MIN));
    }
  };

  const handleZoomSelect = (selectedOption) => {
    if (!selectedOption?.value) return;

    if (selectedOption.value === ZOOM_TYPES.FIT) {
      eventBus.dispatch("caelor-handle-center-view", { type: ZOOM_TYPES.FIT });
    } else if (selectedOption.value === ZOOM_TYPES.FULL) {
      eventBus.dispatch("caelor-handle-center-view", { type: ZOOM_TYPES.FULL });
    }
  };

  const selectValue =
    Math.abs(zoom - 1) < 0.01
      ? ZOOM_OPTIONS.find((opt) => opt.value === ZOOM_TYPES.FULL)
      : zoom === 0
      ? ZOOM_OPTIONS.find((opt) => opt.value === ZOOM_TYPES.FIT)
      : { value: "custom", label: `${Math.round(zoom * 100)}%` };

  const selectedOption = selectValue.value === "custom" ? null : selectValue;

  return (
    <Wrapper>
      <Tooltip content={"Align middle"}>
        <Button
          onClick={() =>
            eventBus.dispatch("caelor-handle-center-view", {
              type: ZOOM_TYPES.ALIGN_MIDDLE,
            })
          }
        >
          <AlignCenterIcon />
        </Button>
      </Tooltip>
      <Divider />
      <Tooltip content={"Zoom out"}>
        <Button onClick={() => handleZoom("out")}>
          <ZoomOutIcon />
        </Button>
      </Tooltip>
      <Tooltip content={"Zoom in"}>
        <Button onClick={() => handleZoom("in")}>
          <ZoomInIcon />
        </Button>
      </Tooltip>
      <div style={{ minWidth: "120px", marginLeft: 8 }}>
        <Select
          options={ZOOM_OPTIONS}
          onChange={handleZoomSelect}
          isSearchable={false}
          menuPlacement="top"
          styles={customStyles}
          value={selectedOption}
          placeholder={selectValue.label}
          components={{ SingleValue: CustomSingleValue }}
        />
      </div>
    </Wrapper>
  );
}

export default ZoomController;
